import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReadingStats, SessionResult } from '../types';
import { ContentSection } from '../types/content';
import { ChapterInfo } from '../utils/indexedDB';
import { MembershipTier } from '../types/membership';
import { SyncStatus } from '../services/syncService';
import { supabase, isSupabaseConfigured, unlockAchievement, fetchUserTierFromSupabase } from '../services/supabaseClient';
import { getUserProfile as getLocalUserProfile, saveUserProfile, updateUserProfile, UserProfile } from '../utils/localUserProfile';
import { updateReadingStats, upsertDailyReadingStat, getTimeAggregates } from '../utils/stats';
import { calculateTotalXP } from '../utils/xp';
import { checkAchievements } from '../utils/achievements';
import { Achievement } from '../data/achievements';
import { updateChallengeProgressFromSession } from '../utils/challengeTracking';
import { saveReadingSession } from '../utils/indexedDB';
import { useMultiBookProgress } from '../hooks/useMultiBookProgress';
import { syncToCloud } from '../services/syncService';

// ============================================================================
// Context Type Definition
// ============================================================================

export interface AppContextValue {
  // Auth
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  isAuthResolved: boolean;
  userTier: MembershipTier;
  setUserTier: React.Dispatch<React.SetStateAction<MembershipTier>>;
  userId: string | null;
  setUserId: React.Dispatch<React.SetStateAction<string | null>>;

  // UI
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  wpm: number;
  setWpm: React.Dispatch<React.SetStateAction<number>>;

  // Stats
  stats: ReadingStats;
  setStats: React.Dispatch<React.SetStateAction<ReadingStats>>;

  // Sync
  syncStatus: SyncStatus;

  // Reading content
  readingContent: string;
  readingSections: ContentSection[] | undefined;
  readingCategoryId: string | undefined;
  readingFormat: string | undefined;
  currentBookTitle: string;
  currentBookId: string;
  currentBookMetadata: {
    totalWords?: number;
    author?: string;
    coverUrl?: string;
    chapters?: ChapterInfo[];
    sections?: ContentSection[];
    categoryId?: string;
  } | null;
  initialWordIndex: number | undefined;

  // Handlers
  handleLogout: () => Promise<void>;
  handleStartReading: (
    content: string,
    sections?: ContentSection[],
    categoryId?: string,
    bookId?: string,
    bookTitle?: string,
    bookMetadata?: {
      totalWords?: number;
      author?: string;
      coverUrl?: string;
      chapters?: ChapterInfo[];
      sections?: ContentSection[];
      categoryId?: string;
    },
    initialWordIndex?: number,
    format?: string
  ) => void;
  handleSessionComplete: (result: SessionResult) => Promise<void>;

  // Achievement modal
  unlockedAchievement: Achievement | null;
  setUnlockedAchievement: React.Dispatch<React.SetStateAction<Achievement | null>>;

  // Tour
  showTour: boolean;
  setShowTour: React.Dispatch<React.SetStateAction<boolean>>;
  hasSeenTour: boolean;
  setHasSeenTour: React.Dispatch<React.SetStateAction<boolean>>;

  // Onboarding signup handler
  handleOnboardingSignup: () => void;
}

// ============================================================================
// Context Creation
// ============================================================================

const AppContext = createContext<AppContextValue | null>(null);

export function useAppContext(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}

// ============================================================================
// Provider Component
// ============================================================================

interface AppProviderProps {
  children: React.ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { saveBookProgress } = useMultiBookProgress();

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthResolved, setIsAuthResolved] = useState(!isSupabaseConfigured);
  const [userTier, setUserTier] = useState<MembershipTier>('guest');
  const [userId, setUserId] = useState<string | null>(null);

  // UI state
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [wpm, setWpm] = useState(300);

  // Stats state
  const [stats, setStats] = useState<ReadingStats>({
    wordsRead: 0,
    timeSpentSeconds: 0,
    lastWpm: 300,
    maxWpm: 0,
    avgWpm: 0,
    sessions: 0,
    history: [],
    booksCompleted: 0,
    genresRead: [],
    sourcesRead: [],
    formatsRead: [],
    timeOfDaySessions: { morning: 0, afternoon: 0, evening: 0, night: 0 },
    challengesCompleted: 0,
    perfectDaysCount: 0,
    peakDailyWords: 0,
    achievementsUnlocked: 0,
    highWpmStreak: 0,
    streakMultiplier: 1,
    lastActiveDate: '',
    longestStreak: 0,
    newRecord: false,
    streakRecovery: false,
    streakBounceBack: false,
    todaySeconds: 0,
    todayWords: 0,
    weekSeconds: 0,
    monthSeconds: 0,
  });

  const [syncStatus, setSyncStatus] = useState<SyncStatus>('synced');

  // Reading content state
  const [readingContent, setReadingContent] = useState('');
  const [readingSections, setReadingSections] = useState<ContentSection[] | undefined>(undefined);
  const [readingCategoryId, setReadingCategoryId] = useState<string | undefined>(undefined);
  const [readingFormat, setReadingFormat] = useState<string | undefined>(undefined);
  const [currentBookTitle, setCurrentBookTitle] = useState('');
  const [currentBookId, setCurrentBookId] = useState('');
  const [currentBookMetadata, setCurrentBookMetadata] = useState<{
    totalWords?: number;
    author?: string;
    coverUrl?: string;
    chapters?: ChapterInfo[];
    sections?: ContentSection[];
    categoryId?: string;
  } | null>(null);
  const [initialWordIndex, setInitialWordIndex] = useState<number | undefined>(undefined);

  // Achievement modal state
  const [unlockedAchievement, setUnlockedAchievement] = useState<Achievement | null>(null);

  // Tour state
  const [showTour, setShowTour] = useState(false);
  const [hasSeenTour, setHasSeenTour] = useState(true);

  // Ref for refreshUserTier to avoid stale closures in auth subscription
  const refreshUserTierRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // ============================================================================
  // Initialize Theme
  // ============================================================================
  useEffect(() => {
    const savedTheme = localStorage.getItem('flashread_theme') as 'light' | 'dark' | null;
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark');
    }
  }, []);

  // ============================================================================
  // Check URL path for password reset
  // ============================================================================
  useEffect(() => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    if (path.includes('reset-password') || hash.includes('access_token')) {
      navigate('/reset-password', { replace: true });
    }
  }, [navigate]);

  // ============================================================================
  // Reusable tier fetch function
  // ============================================================================
  const refreshUserTier = useCallback(async () => {
    if (!isSupabaseConfigured) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const tierInfo = await fetchUserTierFromSupabase(user.id);
        setUserTier(tierInfo.tier);
        setUserId(user.id);

        if (tierInfo.isExpired && tierInfo.tier !== 'guest') {
          await updateUserProfile(user.id, { membershipTier: 'guest' });
          setUserTier('guest');
        }

        const existingProfile = await getLocalUserProfile(user.id);
        if (existingProfile) {
          await updateUserProfile(user.id, {
            membershipTier: tierInfo.tier,
            tierExpiresAt: tierInfo.expiresAt,
          });
        } else {
          const defaultProfile: UserProfile = {
            userId: user.id,
            membershipTier: tierInfo.tier,
            totalXp: 0,
            currentLevel: 1,
            lifetimeXp: 0,
            currentStreak: 0,
            longestStreak: 0,
            lastActiveDate: new Date().toISOString(),
            tierExpiresAt: tierInfo.expiresAt,
            freezesAvailable: 0,
            freezesUsed: 0,
            booksCompleted: 0,
            streakMultiplier: 1,
            challengesCompleted: 0,
            dailyChallengeStreak: 0,
          };
          await saveUserProfile(defaultProfile);
        }
      }
    } catch (error) {
      console.error('Failed to refresh user tier:', error);
    }
  }, []);

  // Keep ref in sync
  useEffect(() => {
    refreshUserTierRef.current = refreshUserTier;
  }, [refreshUserTier]);

  // ============================================================================
  // Initialize Auth via Supabase
  // ============================================================================
  useEffect(() => {
    if (!isSupabaseConfigured) {
      setIsAuthResolved(true);
      return;
    }

    let isMounted = true;

    // Check active session immediately
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!isMounted) return;
      if (!error && session) {
        setIsAuthenticated(true);
      }
      setIsAuthResolved(true);
    }).catch(err => {
      console.warn("Auth session check failed:", err);
      if (isMounted) setIsAuthResolved(true);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);

      if (event === 'TOKEN_REFRESHED' && session) {
        refreshUserTierRef.current();
      }

      if (!session) {
        setReadingContent('');
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // ============================================================================
  // Fetch user tier when authenticated
  // ============================================================================
  useEffect(() => {
    if (!isAuthenticated || !isSupabaseConfigured) {
      setUserTier('guest');
      setUserId(null);
      return;
    }
    refreshUserTier();
  }, [isAuthenticated, refreshUserTier]);

  // ============================================================================
  // Periodic tier re-validation every 15 minutes
  // ============================================================================
  useEffect(() => {
    if (!isAuthenticated || !isSupabaseConfigured) return;

    const FIFTEEN_MINUTES = 15 * 60 * 1000;
    const intervalId = setInterval(() => {
      refreshUserTierRef.current();
    }, FIFTEEN_MINUTES);

    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  // ============================================================================
  // Re-validate tier when tab regains visibility
  // ============================================================================
  useEffect(() => {
    if (!isAuthenticated || !isSupabaseConfigured) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUserTierRef.current();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated]);

  // ============================================================================
  // Apply Theme
  // ============================================================================
  useEffect(() => {
    localStorage.setItem('flashread_theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // ============================================================================
  // Scroll to top on route change
  // ============================================================================
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // ============================================================================
  // Load stats from localStorage
  // ============================================================================
  useEffect(() => {
    const savedStats = localStorage.getItem('flashread_stats');
    if (savedStats) {
      try {
        const parsed = JSON.parse(savedStats);
        if (!parsed.history) parsed.history = [];
        if (parsed.booksCompleted === undefined) parsed.booksCompleted = 0;
        if (parsed.genresRead === undefined) parsed.genresRead = [];
        if (parsed.sourcesRead === undefined) parsed.sourcesRead = [];
        if (parsed.formatsRead === undefined) parsed.formatsRead = [];
        if (parsed.timeOfDaySessions === undefined) parsed.timeOfDaySessions = { morning: 0, afternoon: 0, evening: 0, night: 0 };
        if (parsed.challengesCompleted === undefined) parsed.challengesCompleted = 0;
        if (parsed.perfectDaysCount === undefined) parsed.perfectDaysCount = 0;
        if (parsed.peakDailyWords === undefined) parsed.peakDailyWords = 0;
        if (parsed.achievementsUnlocked === undefined) parsed.achievementsUnlocked = 0;
        if (parsed.highWpmStreak === undefined) parsed.highWpmStreak = 0;
        if (parsed.streakMultiplier === undefined) parsed.streakMultiplier = 1;
        if (parsed.lastActiveDate === undefined) parsed.lastActiveDate = '';
        if (parsed.longestStreak === undefined) parsed.longestStreak = 0;
        if (parsed.newRecord === undefined) parsed.newRecord = false;
        if (parsed.streakRecovery === undefined) parsed.streakRecovery = false;
        if (parsed.streakBounceBack === undefined) parsed.streakBounceBack = false;
        if (parsed.todaySeconds === undefined) parsed.todaySeconds = 0;
        if (parsed.todayWords === undefined) parsed.todayWords = 0;
        if (parsed.weekSeconds === undefined) parsed.weekSeconds = 0;
        if (parsed.monthSeconds === undefined) parsed.monthSeconds = 0;
        setStats(parsed);
      } catch (e) {
        console.error("Failed to parse stats", e);
      }
    }
    const savedWpm = localStorage.getItem('flashread_wpm');
    if (savedWpm) {
      setWpm(parseInt(savedWpm));
    }
    setHasSeenTour(true);
  }, []);

  // ============================================================================
  // Save stats to localStorage
  // ============================================================================
  useEffect(() => {
    localStorage.setItem('flashread_stats', JSON.stringify(stats));
  }, [stats]);

  useEffect(() => {
    localStorage.setItem('flashread_wpm', wpm.toString());
  }, [wpm]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const toggleTheme = useCallback(() => setTheme(prev => prev === 'light' ? 'dark' : 'light'), []);

  const handleLogout = useCallback(async () => {
    if (isAuthenticated && userTier === 'ultra' && userId) {
      try {
        await syncToCloud(userId, userTier);
      } catch (err) {
        console.error('Logout sync failed:', err);
      }
    }

    if (isSupabaseConfigured) {
      await supabase.auth.signOut();
    }
    setIsAuthenticated(false);
    navigate('/');
  }, [isAuthenticated, userTier, userId, navigate]);

  const handleSessionComplete = useCallback(async (result: SessionResult) => {
    // Track free stories used by guests
    if (!isAuthenticated) {
      const oldKey = parseInt(localStorage.getItem('flashread_guest_sessions') || '0', 10);
      const newKey = parseInt(localStorage.getItem('flashread_free_stories_used') || '0', 10);
      const currentCount = Math.max(oldKey, newKey);
      const newCount = currentCount + 1;
      localStorage.setItem('flashread_free_stories_used', newCount.toString());
      localStorage.setItem('flashread_guest_sessions_remaining', Math.max(0, 3 - newCount).toString());
      if (newCount >= 3) {
        setTimeout(() => navigate('/upsell'), 3000);
      }
    }

    // Save per-book progress
    let bookCompleted = false;
    if (currentBookId && isAuthenticated) {
      try {
        const chapterTitle = result.chapterId ? `Chapter ${result.chapterId}` : 'Current Chapter';
        const newWordIndex = (result.startWordIndex || 0) + result.wordsRead;
        bookCompleted = await saveBookProgress(
          currentBookId,
          currentBookTitle || 'Unknown',
          result.chapterId || 'main',
          chapterTitle,
          newWordIndex,
          result.wordsRead,
          currentBookMetadata || undefined
        );
      } catch (error) {
        console.error('Failed to save book progress:', error);
      }
    }

    const dailyInfo = await upsertDailyReadingStat(result);
    const todayAvgWpm = dailyInfo.totalSeconds > 0
      ? (dailyInfo.totalWords / (dailyInfo.totalSeconds / 60))
      : 0;

    const timeAggregates = await getTimeAggregates(result.date);

    let updatedCurrentStreak = 0;
    let profile: UserProfile | null = null;
    let currentUserId: string | null = null;
    let oldCurrentStreak = 0;

    if (isAuthenticated) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        currentUserId = user.id;
        profile = await getLocalUserProfile(user.id);
        oldCurrentStreak = profile?.currentStreak ?? 0;

        const xpEarned = calculateTotalXP(
          result.wordsRead,
          result.avgWpm,
          oldCurrentStreak,
          { isNewMaxWPM: result.maxWpm > (stats.maxWpm || 0) }
        );

        if (profile) {
          const newTotalXp = profile.totalXp + xpEarned;
          const newLifetimeXp = profile.lifetimeXp + xpEarned;
          const newLevel = Math.floor(newTotalXp / 100) + 1;
          const leveledUp = newLevel > profile.currentLevel;

          await updateUserProfile(user.id, {
            totalXp: newTotalXp,
            lifetimeXp: newLifetimeXp,
            currentLevel: newLevel,
          });

          if (leveledUp) {
            console.log(`🎉 Level up! Now at level ${newLevel}`);
          }
        }

        if (profile) {
          const today = new Date().toISOString().split('T')[0];
          const lastActive = profile.lastActiveDate ? profile.lastActiveDate.split('T')[0] : null;
          let newStreak = profile.currentStreak;

          if (lastActive === today) {
            newStreak = profile.currentStreak;
          } else if (lastActive === new Date(Date.now() - 86400000).toISOString().split('T')[0]) {
            newStreak = profile.currentStreak + 1;
          } else {
            newStreak = 1;
          }

          const newLongestStreak = Math.max(profile.longestStreak, newStreak);
          await updateUserProfile(user.id, {
            currentStreak: newStreak,
            longestStreak: newLongestStreak,
            lastActiveDate: new Date().toISOString(),
          });
          updatedCurrentStreak = newStreak;
        }
      }
    }

    const newStats = updateReadingStats(stats, result, {
      bookCompleted,
      genre: readingCategoryId,
      source: currentBookId,
      format: readingFormat,
      todayAvgWpm,
      currentStreak: updatedCurrentStreak || undefined,
      todaySeconds: timeAggregates.todaySeconds,
      todayWords: timeAggregates.todayWords,
      weekSeconds: timeAggregates.weekSeconds,
      monthSeconds: timeAggregates.monthSeconds,
    });

    newStats.peakDailyWords = Math.max(newStats.peakDailyWords || 0, dailyInfo.totalWords);

    if (isAuthenticated && currentUserId) {
      const { newlyCompleted, newlyPerfectDay } = await updateChallengeProgressFromSession(result, oldCurrentStreak, currentUserId);
      newStats.challengesCompleted += newlyCompleted.length;
      if (newlyPerfectDay) {
        newStats.perfectDaysCount += 1;
      }

      await saveReadingSession({
        userId: currentUserId,
        wordsRead: result.wordsRead,
        durationSeconds: result.durationSeconds,
        avgWpm: result.avgWpm,
        maxWpm: result.maxWpm,
        sessionDate: new Date(result.date).toISOString().split('T')[0],
        createdAt: new Date().toISOString(),
        synced: false,
      });

      if (profile) {
        const { newlyUnlocked } = checkAchievements(
          newStats,
          profile as any,
          updatedCurrentStreak,
          newStats.sessions
        );
        if (newlyUnlocked.length > 0) {
          setUnlockedAchievement(newlyUnlocked[0]);
          for (const achievement of newlyUnlocked) {
            unlockAchievement(currentUserId, achievement.id, achievement.xpReward)
              .catch(err => console.error('Achievement sync failed:', err));
          }
        }
        newStats.achievementsUnlocked += newlyUnlocked.length;
      }
    }

    setStats(newStats);
  }, [isAuthenticated, currentBookId, currentBookTitle, currentBookMetadata, readingCategoryId, readingFormat, stats, navigate]);

  const handleStartReading = useCallback((
    content: string,
    sections?: ContentSection[],
    categoryId?: string,
    bookId?: string,
    bookTitle?: string,
    bookMetadata?: {
      totalWords?: number;
      author?: string;
      coverUrl?: string;
      chapters?: ChapterInfo[];
      sections?: ContentSection[];
      categoryId?: string;
    },
    initWordIndex?: number,
    format?: string
  ) => {
    // Navigate FIRST with content in location.state to avoid race condition.
    // The ReaderGuard will check location.state as a fallback.
    navigate('/reader', {
      state: {
        readingContent: content,
        readingSections: sections,
        readingCategoryId: categoryId,
        readingFormat: format,
        currentBookId: bookId || '',
        currentBookTitle: bookTitle || '',
        currentBookMetadata: bookMetadata || null,
        initialWordIndex: initWordIndex,
      }
    });

    // Set context state AFTER navigation. These will be available on re-render.
    setReadingContent(content);
    setReadingSections(sections);
    setReadingCategoryId(categoryId);
    setReadingFormat(format);
    setCurrentBookId(bookId || '');
    setCurrentBookTitle(bookTitle || '');
    setCurrentBookMetadata(bookMetadata || null);
    setInitialWordIndex(initWordIndex);
  }, [navigate]);

  const handleOnboardingSignup = useCallback(() => {
    setShowTour(false);
    setHasSeenTour(true);
    navigate('/signup');
  }, [navigate]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value: AppContextValue = {
    isAuthenticated,
    setIsAuthenticated,
    isAuthResolved,
    userTier,
    setUserTier,
    userId,
    setUserId,
    theme,
    toggleTheme,
    wpm,
    setWpm,
    stats,
    setStats,
    syncStatus,
    readingContent,
    readingSections,
    readingCategoryId,
    readingFormat,
    currentBookTitle,
    currentBookId,
    currentBookMetadata,
    initialWordIndex,
    handleLogout,
    handleStartReading,
    handleSessionComplete,
    unlockedAchievement,
    setUnlockedAchievement,
    showTour,
    setShowTour,
    hasSeenTour,
    setHasSeenTour,
    handleOnboardingSignup,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}
